import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { nanoid } from "nanoid";
import pointsWithinPolygon from "@turf/points-within-polygon";

import { aoiCategories } from "../config/config.aoi";
import { initialDataState } from "../config/config.data";
import { entities } from "../config/config.dataExplorer";
import { isJsonString } from "../utils";

export const getData = createAsyncThunk(
  "data/getData",
  async (
    { dataId, source, bypassRefresh = false, columnsToFetch = [] },
    thunkAPI
  ) => {
    console.log("getData", source);
    const dataState = thunkAPI.getState().data;
    let data = dataState[dataId]?.data;
    let metadata = null;
    let version;

    // unless data is a flat file, always refetch
    if (!data || (entities[dataId] && !bypassRefresh)) {
      // fetch data
      console.log("Fetching data...", dataId);
      let response;

      // logic for fetching sites from NWDi api
      if (dataId === "nonNgwosSites") {
        if (columnsToFetch.length > 0) {
          response = await axios({
            url: dataState[dataId].location,
            params: {
              attributes: columnsToFetch.join(","),
            },
          });
          data = response.data?.sites;
          version = nanoid();
        }
      }
      // logic specifically for NGWOS API resources, parsing and paginating
      else if (entities[dataId]) {
        response = await axios.get(dataState[dataId].location);
        const key =
          entities[dataId].responseBodyKey || dataState[dataId].endpoint;
        data = response.data[key];

        // if response has a next key, fetch the next page
        while (response.data.next) {
          const nextUrl = `${dataState[dataId].location}?skip=${response.data.next}`;
          console.log(`Fetching next page of ${dataState[dataId].endpoint}...`);
          const nextResponse = await axios.get(nextUrl);
          data = data.concat(nextResponse.data[key]);
          response.data = nextResponse.data;
        }
        version = nanoid();
      } else {
        response = await axios.get(dataState[dataId].location);
        data = response.data;
      }
      metadata = response?.data?.metadata || null;
    }
    let filteredData = data || [];

    return {
      dataName: dataId,
      data,
      metadata,
      filteredData,
      version,
    };
  }
);

// merge new data into existing data based on a unique identifier (e.g. site_number)
export function mergeDataMiddleware({ getState }) {
  return (next) => (action) => {
    if (action.type === "data/getData/fulfilled") {
      const { dataName } = action.payload;
      const dataObj = getState().data[dataName];
      // skip if not the NWDi api
      if (dataName !== "nonNgwosSites") {
        return next(action);
      }

      const data = dataObj.data ? { ...dataObj.data } : null;
      const newData = action.payload.data;

      if (data && newData) {
        if (dataObj.uniqueIdField) {
          data.features = [...data.features]?.map((feat) => {
            const newSiteData = newData[feat.properties[dataObj.uniqueIdField]];
            if (!newSiteData) {
              return feat;
            }
            // if latitude or longitude are available, update coordinates
            let geometry = { ...feat.geometry };
            if (newSiteData.latitude && newSiteData.longitude) {
              geometry.coordinates = [
                parseFloat(newSiteData.longitude),
                parseFloat(newSiteData.latitude),
              ];
            }
            const newProperties = {};
            Object.keys(newSiteData).forEach((key) => {
              const value = newSiteData[key];
              if (typeof value === "number" || !isNaN(parseFloat(value))) {
                newProperties[key] = value + "";
              } else if (isJsonString(value)) {
                newProperties[key] = JSON.parse(value);
              } else {
                newProperties[key] = value;
              }
            });
            return {
              ...feat,
              properties: {
                ...feat.properties,
                ...newProperties,
              },
              geometry,
            };
          });
          // update data in action payload
          action.payload.data = data;
        } else {
          console.warn(
            "Warning: no uniqueIdField defined for data source",
            dataName
          );
        }
      } else if (newData) {
        action.payload.data = {
          type: "FeatureCollection",
          features: Object.keys(newData).map((uniqueKey) => {
            let properties = {};
            Object.keys(newData[uniqueKey]).forEach((key) => {
              const value = newData[uniqueKey][key];
              if (typeof value === "number" || !isNaN(parseFloat(value))) {
                properties[key] = value + "";
              } else if (isJsonString(value)) {
                properties[key] = JSON.parse(value);
              } else {
                properties[key] = value;
              }
            });

            return {
              type: "Feature",
              properties,
              geometry: {
                type: "Point",
                coordinates: [
                  parseFloat(newData[uniqueKey]?.longitude),
                  parseFloat(newData[uniqueKey]?.latitude),
                ],
              },
            };
          }),
        };
      }
    }
    return next(action);
  };
}

// Filter data based on current app state
export function filterDataMiddleware({ getState }) {
  return (next) => (action) => {
    if (action.type === "data/getData/fulfilled") {
      const { dataName } = action.payload;
      const dataId = dataName;
      const dataObj = getState().data[dataName];
      const layerState = getState().layers;
      const polygonState = getState().polygon;
      const aoiState = getState().aoi;
      const dataState = getState().data;

      if (!dataObj.filterable) {
        return next(action);
      }

      // Perform the filtering logic here using the latest state
      // and action.payload, then modify action.payload as needed
      // Filter data
      let data = action.payload.data;
      if (data) {
        let filteredData = data?.features;

        const activeLayerIds = Object.keys(layerState || {}).filter(
          (id) => layerState[id].data === dataId && layerState[id].showLayer
        );

        activeLayerIds.forEach((layerId) => {

          // handle special case for NWDi api sites - filter out sites outside of ngwos basins
          if(dataId === "nonNgwosSites"){
            filteredData = filteredData.filter(f => f.properties.ngwos_basin)
          }

          // Apply polygon filters
          const selectedPolygons = polygonState.selectedFeatureIndexes.map(
            (index) =>
              index <= polygonState.features.features.length - 1
                ? polygonState.features.features[index]
                : null
          );
          if (selectedPolygons.length > 0) {
            // Handling polygon multi-selection
            // 1. filter full dataset on each polygon
            // 2. concatenate filtered feature arrays
            // 3. remove duplicate features (in the case of overlapping polygons)
            filteredData = [];
            let filteredDataSinglePolygon = null;

            selectedPolygons.forEach((polygon) => {
              if (polygon) {
                filteredDataSinglePolygon = pointsWithinPolygon(data, polygon);
                filteredData.push(...filteredDataSinglePolygon.features);
              }
            });

            const onlyUniqueFeatures = (feature, index, self) => {
              return self.indexOf(feature) === index;
            };
            filteredData = filteredData.filter(onlyUniqueFeatures);
          }

          // Filter out empty or "No Data" values
          const colorByProperty = layerState[layerId].properties.find(
            (p) => layerState[layerId].colorValue === p.id
          );

          if (!colorByProperty.showNoData) {
            filteredData = filteredData.filter((d) => {
              if (Array.isArray(d.properties[colorByProperty.id])) {
                return d.properties[colorByProperty.id].length > 0;
              } else {
                return (
                  d.properties[colorByProperty.id] &&
                  d.properties[colorByProperty.id] !== "No Data" &&
                  d.properties[colorByProperty.id] !== "null" &&
                  d.properties[colorByProperty.id] !== "Not Applicable"
                );
              }
            });
          }

          // Apply layer filters
          Object.keys(layerState[layerId].activeFilters || {}).forEach(
            (filterProperty) => {
              filteredData = filteredData.filter((d) => {
                if (Array.isArray(d.properties[filterProperty])) {
                  // check that intersection length is > 0
                  const shouldKeep =
                    layerState[layerId].activeFilters[filterProperty].filter(
                      (v) => d.properties[filterProperty].includes(v)
                    ).length > 0;
                  if (!colorByProperty.showNoData) {
                    return shouldKeep;
                  } else {
                    return (
                      shouldKeep ||
                      d.properties[filterProperty].includes("No Data")
                    );
                  }
                } else {
                  const shouldKeep = layerState[layerId].activeFilters[
                    filterProperty
                  ].includes(d.properties[filterProperty]);
                  if (!colorByProperty.showNoData) {
                    return shouldKeep;
                  } else {
                    return (
                      shouldKeep || d.properties[filterProperty] === "No Data"
                    );
                  }
                }
              });
            }
          );
        });

        // If AOI is selected, filter data to AOI
        if (
          aoiState.selection.category &&
          aoiState.selection.selectedIds.length > 0
        ) {
          // Apply AOI filter
          const dataFieldName = aoiCategories.find(
            (cat) => cat.id === aoiState.selection.category
          ).dataFieldName;
          const aoiCategoryName = aoiCategories.find(
            (cat) => cat.id === aoiState.selection.category
          ).data;

          filteredData = filteredData.filter((d) => {
            if (d.properties.hasOwnProperty(dataFieldName)) {
              const aoiIds = aoiState.selection.selectedIds;
              // check if data layer value is string
              if (typeof d.properties[dataFieldName] === "string") {
                const aoiFeatures = dataState[
                  aoiCategoryName
                ].data.features.filter((feature) =>
                  aoiIds.includes(feature.id)
                );
                const aoiNames = aoiFeatures.map((f) => f.properties.name);
                return aoiNames.includes(d.properties[dataFieldName]);
              } else {
                // case for networks where one site has an array of
                //  network names; retain if array contains selected network name
                return d.properties[dataFieldName]?.includes(
                  aoiCategoryName.features.find((feature) =>
                    aoiIds.includes(feature.id)
                  ).properties.name
                );
              }
            } else {
              return d;
            }
          });
        }
        // Add filteredData to the action's payload
        action.payload.filteredData = filteredData;

        // update data version if previous filteredData length is different from current filteredData length
        // if (dataState[dataName].filteredData?.length !== filteredData.length) {
        //   action.payload.version = nanoid();
        // }
      } else {
        // if data is not yet available (e.g. a duplicate request while another request is pending), return empty array
        action.payload.filteredData = [];
      }
    }
    return next(action);
  };
}

export const dataSlice = createSlice({
  name: "data",
  initialState: initialDataState,
  reducers: {
    setData(state, action) {
      const { dataName, data } = action.payload;
      state[dataName].data = data;
    },
    setFilteredSiteData(state, action) {
      state["sites"].filteredData = action.payload;
    },
    updateProjectPeople(state, action) {
      const { id, people } = action.payload;
      for (let i = 0; i < state.projects.data.length; i++) {
        if (state.projects.data[i].id === id) {
          state.projects.data[i].people = people;
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getData.fulfilled, (state, action) => {
      const { dataName, data, metadata, version } = action.payload;
      // update data and fetch status
      state[dataName].data = data;
      state[dataName].fetching = false;
      state[dataName].fetched = true;
      // update metadata if supplied
      if (metadata) {
        state[dataName].metadata = metadata;
      }
      // update version if a new version was provided
      if (version) {
        state[dataName].version = version;
      }
    });
    builder.addCase(getData.pending, (state, action) => {
      const { dataId } = action.meta.arg;
      state[dataId].fetching = true;
    });
  },
});
